import { useCallback, useSyncExternalStore } from "react";
import type { ThemeMode } from "../../../shared/types";

const STORAGE_KEY = "palnia_theme";
const VALID_THEMES: readonly ThemeMode[] = ["light", "dark", "system"] as const;

type ResolvedTheme = "light" | "dark";

interface ThemeStore {
  theme: ThemeMode;
}

function loadTheme(): ThemeMode {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && (VALID_THEMES as readonly string[]).includes(stored)) {
    return stored as ThemeMode;
  }
  return "system";
}

const themeStore: ThemeStore = {
  theme: loadTheme(),
};

const listeners = new Set<() => void>();
let mediaQuery: MediaQueryList | null = null;

function initMediaQuery() {
  if (typeof window === "undefined") return;
  if (!mediaQuery) {
    mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    mediaQuery.addEventListener("change", () => {
      if (themeStore.theme === "system") {
        notifyListeners();
      }
    });
  }
}

function notifyListeners() {
  for (const listener of listeners) { try { listener(); } catch { /* ignore stale listener during teardown */ } }
}

function getResolvedTheme(): ResolvedTheme {
  if (themeStore.theme === "system") {
    if (!mediaQuery) initMediaQuery();
    return mediaQuery?.matches ? "dark" : "light";
  }
  return themeStore.theme;
}

function applyTheme() {
  if (typeof window === "undefined") return;
  const resolved = getResolvedTheme();
  const root = document.documentElement;
  if (resolved === "dark") {
    root.classList.add("dark");
    root.classList.remove("light");
  } else {
    root.classList.add("light");
    root.classList.remove("dark");
  }
}

function setTheme(newTheme: ThemeMode) {
  themeStore.theme = newTheme;
  localStorage.setItem(STORAGE_KEY, newTheme);
  applyTheme();
  notifyListeners();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  initMediaQuery();
  applyTheme();
  return () => listeners.delete(listener);
}

function getSnapshot(): ThemeMode {
  return themeStore.theme;
}

applyTheme();

export function useTheme() {
  const theme = useSyncExternalStore(subscribe, getSnapshot);
  const resolved = getResolvedTheme();

  return {
    theme,
    resolvedTheme: resolved,
    setTheme: useCallback((newTheme: ThemeMode) => setTheme(newTheme), []),
  };
}
